import React from 'react';
import { Button, Tag, Typography } from 'antd';
import { EditOutlined, ExportOutlined, TruckOutlined } from '@ant-design/icons';
import type { TransportRouteDetailDto } from '../../../dto/transport';
import { formatDate } from '../../../utils/dateUtils';
import { formatLocalizedNumber, formatLocalizedQuantity } from '../../../utils/numberFormatting';
import { buildTransportRouteLabel } from '../../orders/transportAddressUtils';
import { TransportAllocatedCost } from '../TransportAllocatedCost';
import { useTranslation } from 'react-i18next';
import './TransportOrderRealizationCard.css';
import { formatCargoQuantityPair } from '../../../utils/cargoQuantity';
import { formatUnit } from '../../../utils/units';
import {
  getTransportStatusDisplay,
  getTransportStatusDisplayTranslationKey,
} from '../transportRouteViews';
import { getTransportLabel } from '../../../utils/transportLabel';

const { Text } = Typography;
const STATUS_COLORS: Record<string, string> = {
  planned: 'blue',
  inProgress: 'gold',
  completed: 'green',
  cancelled: 'red',
};

interface Props {
  transport: TransportRouteDetailDto;
  orderId: string;
  showCosts?: boolean;
  showOrderIdentity?: boolean;
  showRouteContext?: boolean;
  onOpen?: () => void;
  onOpenOrder?: () => void;
  onUnlink?: () => void;
  onEditStatus?: () => void;
}

/** Presentation only: cargo and all delivery facts are backend-owned. */
export const TransportOrderRealizationCard: React.FC<Props> = ({
  transport,
  orderId,
  showCosts = false,
  showOrderIdentity = false,
  showRouteContext = true,
  onOpen,
  onOpenOrder,
  onUnlink,
  onEditStatus,
}) => {
  const { t, i18n } = useTranslation(['transport', 'common']);
  const currentCargo = transport.cargoItems.filter((item) => item.orderId === orderId);
  const otherOrders = transport.routeOrders.filter((item) => item.orderId !== orderId);
  const routeOrder = transport.routeOrders.find((item) => item.orderId === orderId);
  const routeLabel = buildTransportRouteLabel(transport.loadingAddresses, transport.deliveryAddresses, transport.originCity, transport.destinationCity).label;
  const displayStatus = getTransportStatusDisplay(transport.status);
  const usefulMetrics = routeOrder ? [
    routeOrder.packages != null && routeOrder.packages > 0 ? `${t('transport:packages')}: ${formatLocalizedNumber(routeOrder.packages, 0, i18n.language)}` : null,
    routeOrder.ldm != null && routeOrder.ldm > 0 ? `LDM: ${formatLocalizedNumber(routeOrder.ldm, 2, i18n.language)}` : null,
    routeOrder.weight != null && routeOrder.weight > 0 ? `${t('transport:weight')}: ${formatLocalizedNumber(routeOrder.weight, 2, i18n.language)} kg` : null,
    routeOrder.volume != null && routeOrder.volume > 0 ? `${t('transport:volume')}: ${formatLocalizedNumber(routeOrder.volume, 3, i18n.language)} m³` : null,
    routeOrder.allocationPercent != null && routeOrder.allocationPercent > 0 ? `${t('transport:allocation.percent')}: ${formatLocalizedNumber(routeOrder.allocationPercent, 2, i18n.language)}%` : null,
  ].filter(Boolean) as string[] : [];
  return <article className={`transport-order-realization-card is-${displayStatus}`}>
    <header className="transport-order-realization-card__header">
      <div className="transport-order-realization-card__identity">
        {showOrderIdentity && routeOrder ? (
          <>
            <Button type="link" className="transport-order-realization-card__order-link" onClick={onOpenOrder}>
              {routeOrder.order.displayOrderNumber || routeOrder.order.orderNumber}
            </Button>
            <Text type="secondary"> · {routeOrder.order.customerName || '—'}</Text>
          </>
        ) : (
          <><span className="transport-order-realization-card__truck"><TruckOutlined /></span><div><strong>{getTransportLabel(transport, t('common:unknown'))}</strong><Text type="secondary">{transport.transportCompany || '—'}</Text></div></>
        )}
      </div>
      <div className="transport-order-realization-card__actions">
        <Tag color={STATUS_COLORS[displayStatus]} bordered={false}>{t(getTransportStatusDisplayTranslationKey(transport.status))}</Tag>
        {onEditStatus && <Button size="small" icon={<EditOutlined />} onClick={onEditStatus}>{t('transport:orderTransports.changeStatus')}</Button>}
        {onOpen && <Button size="small" type="text" icon={<ExportOutlined />} onClick={onOpen}>{t('transport:orderTransports.details')}</Button>}
        {onUnlink && <Button size="small" type="text" danger onClick={onUnlink}>{t('transport:orderTransports.unlink')}</Button>}
      </div>
    </header>
    {showRouteContext && <div className="transport-order-realization-card__journey"><strong>{routeLabel || '—'}</strong><div className="transport-order-realization-card__dates"><span><small>{t('transport:fields.loadingDate')}</small><b>{formatDate(transport.loadingDate) || '—'}</b></span><span><small>{t('transport:fields.estimatedDelivery')}</small><b>{formatDate(transport.estimatedDelivery) || '—'}</b></span>{transport.actualDelivery && <span><small>{t('transport:fields.actualDelivery')}</small><b>{formatDate(transport.actualDelivery)}</b></span>}</div></div>}
    <div className="transport-order-realization-card__cargo-label">{t('transport:orderTransports.cargoInThisOrder')}</div>
    <div className="transport-order-realization-card__cargo">{currentCargo.length ? currentCargo.map((item) => {
      const plannedQuantity = Number(item.quantity || 0);
      const deliveredQuantity = item.deliveredQuantity == null ? null : Number(item.deliveredQuantity);
      const notDeliveredQuantity = deliveredQuantity == null
        ? null
        : Math.max(plannedQuantity - deliveredQuantity, 0);
      const deliveredPercent = deliveredQuantity == null || plannedQuantity <= 0
        ? null
        : Math.min(100, Math.max(0, deliveredQuantity / plannedQuantity * 100));
      const deliveryState = deliveredPercent == null
        ? 'is-pending'
        : deliveredPercent >= 100
          ? 'is-complete'
          : deliveredPercent > 0
            ? 'is-partial'
            : 'is-missing';
      return <div className={`transport-order-realization-card__cargo-row ${deliveryState}`} key={item.id}>
        <div className="transport-order-realization-card__cargo-product">
          <strong>{item.productName}</strong>
        </div>
        <div className="transport-order-realization-card__cargo-quantities">
          <span><small>{t('transport:deliveryConfirmation.planLabel')}</small><b>{formatCargoQuantityPair(item, i18n.language)}</b></span>
          {deliveredQuantity != null ? <span className="transport-order-realization-card__delivered"><small>{t('transport:orderTransports.actuallyDelivered')}</small><b>{formatLocalizedQuantity(deliveredQuantity, i18n.language)} {formatUnit(item.unit, i18n.language)}</b></span> : null}
          {notDeliveredQuantity != null ? <span className="transport-order-realization-card__not-delivered"><small>{t('transport:orderTransports.notDelivered')}</small><b>{formatLocalizedQuantity(notDeliveredQuantity, i18n.language)} {formatUnit(item.unit, i18n.language)}</b></span> : null}
        </div>
        {deliveredPercent != null ? <span className="transport-order-realization-card__cargo-progress" aria-label={`${formatLocalizedQuantity(deliveredPercent, i18n.language)}%`}><i style={{ width: `${deliveredPercent}%` }} /></span> : null}
      </div>;
    }) : <Text type="secondary">{t('transport:orderTransports.noCargoForOrder')}</Text>}</div>
    {usefulMetrics.length > 0 && <div className="transport-order-realization-card__metrics">{usefulMetrics.map((metric) => <span key={metric}>{metric}</span>)}</div>}
    {showCosts && Number(routeOrder?.allocatedTransportCost || 0) > 0 && <TransportAllocatedCost allocatedTransportCost={routeOrder?.allocatedTransportCost as number} originalCurrency={routeOrder?.originalCurrency} originalAmount={routeOrder?.originalAmount} exchangeRateUsed={routeOrder?.exchangeRateUsed} exchangeRateSource={routeOrder?.exchangeRateSource} exchangeRateTableNumber={routeOrder?.exchangeRateTableNumber} exchangeRateRequestedDate={routeOrder?.exchangeRateRequestedDate} exchangeRateEffectiveDate={routeOrder?.exchangeRateEffectiveDate} exchangeRateIsFallback={routeOrder?.exchangeRateIsFallback} />}
    {showRouteContext && otherOrders.length > 0 && <div className="transport-order-realization-card__shared"><Text type="secondary">{t('transport:orderTransports.sharedWithSummary', { count: otherOrders.length })}{otherOrders.slice(0, 2).map((item) => ` · ${item.order.displayOrderNumber || item.order.orderNumber}`).join('')}</Text></div>}
  </article>;
};
